import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import clsx from "clsx";
import { Card } from "@/components/ui/Card";
import { Spinner } from "@/components/ui/Spinner";
import { Alert } from "@/components/ui/Alert";
import { ApiError } from "@/lib/apiClient";
import { useCurrentUser, userHasGroup, userIsBuero } from "@/lib/auth";
import {
  fetchBestellwesenOverview,
  type BestellwesenAnfrageOverviewItem,
  type BestellwesenOrderOverviewItem,
  type BestellwesenOverview,
} from "@/lib/bestellwesenOverview";
import type { MaListItem } from "@/lib/material";
import { useRealtime } from "@/lib/useRealtime";
import { fmtDateTime, labelAnfrageStatus, statusBadgeClass } from "@/pages/anfragen/anfrageWizardUtils";
import { fmtOrderDate } from "@/pages/bestellungen/orderHistoryDisplay";
import {
  fmtMaDateOnly,
  formatRequestAge,
  MaFortschrittBadge,
  maAuftragLabel,
  maIsUrgentUnclaimedFix,
} from "@/pages/material/maListDisplay";

type BestellwesenTileKey = "bw-material-open" | "bw-material-work" | "bw-inquiries" | "bw-orders-late";

interface BestellwesenState {
  overview: BestellwesenOverview | null;
  loading: boolean;
  error: string | null;
}

const HomeBestellwesenContext = createContext<BestellwesenState>({
  overview: null,
  loading: false,
  error: null,
});

export function HomeBestellwesenProvider({
  enabled,
  children,
}: {
  enabled: boolean;
  children: ReactNode;
}) {
  const [overview, setOverview] = useState<BestellwesenOverview | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async (options?: { silent?: boolean }) => {
    if (!options?.silent) setLoading(true);
    setError(null);
    try {
      setOverview(await fetchBestellwesenOverview());
    } catch (err) {
      setError(
        err instanceof ApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : "Die Bestellwesen-Übersicht konnte nicht geladen werden.",
      );
      if (!options?.silent) setOverview(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!enabled) return;
    void reload();
  }, [enabled, reload]);

  useRealtime({
    enabled,
    scopes: ["material-anforderungen:list", "orders:list", "anfragen:list"],
    domains: ["material_anforderungen", "material-anforderungen", "anfragen", "orders"],
    events: [
      "material_anforderungen_changed",
      "material-anforderungen_changed",
      "material_anforderung_changed",
      "anfragen_changed",
      "orders_changed",
    ],
    onChange: () => void reload({ silent: true }),
  });

  return (
    <HomeBestellwesenContext.Provider value={{ overview, loading, error }}>
      {children}
    </HomeBestellwesenContext.Provider>
  );
}

function projectDetail(ma: MaListItem): string {
  const seen = new Set<string>();
  const parts: string[] = [];
  for (const value of [
    ma.auftrag_ort,
    ma.auftrag_ort_zusatz,
    ma.auftrag_beschreibung,
    ma.auftrag_kunde,
    ma.auftrag_ansprechpartner,
  ]) {
    const text = value != null ? String(value).trim() : "";
    if (!text || seen.has(text.toLowerCase())) continue;
    seen.add(text.toLowerCase());
    parts.push(text);
  }
  return parts.join(" · ");
}

function SectionLink({ href, label }: { href: string; label: string }) {
  return (
    <Link
      to={href}
      className="inline-flex items-center rounded-full border border-accent px-3 py-1 text-xs font-medium text-accent hover:bg-accent/10"
    >
      {label}
    </Link>
  );
}

function CountTitle({ title, total }: { title: string; total: number }) {
  return (
    <span className="inline-flex flex-wrap items-center gap-2">
      {title}
      {total > 0 ? (
        <span className="rounded-full bg-accent px-2 py-0.5 text-xs font-semibold tabular-nums text-white">
          {total}
        </span>
      ) : null}
    </span>
  );
}

function TruncatedHint({
  section,
}: {
  section: { total: number; truncated: boolean; items: readonly unknown[] };
}) {
  if (!section.truncated) return null;
  return (
    <p className="border-t border-separator px-4 py-2 text-xs text-label-secondary">
      {section.total} Einträge insgesamt – es werden die ältesten {section.items.length} angezeigt.
    </p>
  );
}

function MaRows({ items }: { items: MaListItem[] }) {
  if (!items.length) return <p className="px-4 py-6 text-center text-sm text-label-secondary">Keine Einträge.</p>;
  return (
    <ul className="eh-bw-list">
      {items.map((ma) => {
        const urgentFix = maIsUrgentUnclaimedFix(ma);
        const detail = projectDetail(ma);
        return (
          <li key={ma.id} className={urgentFix ? "eh-bw-row--urgent" : undefined}>
            <Link to={`/bestellwesen/materialanforderungen?id=${ma.id}`} className="eh-bw-row eh-bw-row--ma">
              <div className="eh-bw-ma-id flex min-w-0 items-center gap-2">
                <span className="truncate font-mono text-accent">{ma.ma_nummer}</span>
                {urgentFix ? <span className="ma-urgent-fix-pill shrink-0">Sofort</span> : null}
              </div>
              <div className="eh-bw-ma-project min-w-0">
                <div className="truncate text-label">{maAuftragLabel(ma)}</div>
                {detail ? (
                  <div className="eh-bw-ma-project__place truncate text-label-secondary" title={detail}>
                    {detail}
                  </div>
                ) : null}
              </div>
              <div className="eh-bw-ma-meta text-label-secondary">
                {ma.liefertermin ? (
                  <span className={ma.liefertermin_fix ? "font-semibold text-status-orange" : "text-label"}>
                    {ma.liefertermin_fix ? "Fix " : ""}
                    {fmtMaDateOnly(ma.liefertermin)}
                  </span>
                ) : (
                  "—"
                )}
              </div>
              <div className="eh-bw-ma-age">
                <span className="shrink-0 whitespace-nowrap rounded-full bg-fill-tertiary px-2 py-0.5 text-xs font-semibold tabular-nums text-label-secondary">
                  {formatRequestAge(ma.created_at)}
                </span>
              </div>
              <div className="eh-bw-ma-status">
                <MaFortschrittBadge f={ma.monteur_fortschritt} status={ma.status} />
              </div>
            </Link>
          </li>
        );
      })}
    </ul>
  );
}

function InquiryRows({ items }: { items: BestellwesenAnfrageOverviewItem[] }) {
  if (!items.length) {
    return <p className="px-4 py-6 text-center text-sm text-label-secondary">Keine offenen Anfragen.</p>;
  }
  return (
    <ul className="eh-bw-list">
      {items.map((row) => {
        const subject = row.subject || "Ohne Betreff";
        return (
          <li key={row.id}>
            <Link to={`/bestellwesen/anfragen?id=${row.id}`} className="eh-bw-row">
              <div className="eh-bw-row__main">
                <p className="truncate text-sm font-medium text-label" title={subject}>
                  {subject}
                </p>
                <p className="mt-0.5 truncate text-xs text-label-secondary">
                  {row.supplier_name || "Kein Lieferant"}
                  <span className="text-label-tertiary"> · </span>
                  {fmtDateTime(row.created_at)}
                </p>
              </div>
              <span
                className={clsx(
                  "inline-flex shrink-0 rounded-full px-2.5 py-0.5 text-xs font-semibold",
                  statusBadgeClass(row.status as "draft" | "ready" | "sent"),
                )}
              >
                {labelAnfrageStatus(row.status as "draft" | "ready" | "sent")}
              </span>
            </Link>
          </li>
        );
      })}
    </ul>
  );
}

function OrderRows({ items }: { items: BestellwesenOrderOverviewItem[] }) {
  if (!items.length) {
    return (
      <p className="px-4 py-6 text-center text-sm text-label-secondary">
        Keine überfälligen Bestellungen.
      </p>
    );
  }
  return (
    <ul className="eh-bw-list">
      {items.map((row) => {
        const title = row.bestellnummern || row.subject || `Bestellung #${row.id}`;
        const age = formatRequestAge(row.created_at) || `${row.days_open} Tage`;
        return (
          <li key={row.id}>
            <Link to="/bestellwesen/bestellungen" className="eh-bw-row">
              <div className="eh-bw-row__main">
                <p className="truncate font-mono text-[13px] font-semibold text-accent" title={title}>
                  {title}
                </p>
                <p className="mt-0.5 truncate text-xs text-label-secondary">
                  {row.supplier_name || "Kein Lieferant"}
                  <span className="text-label-tertiary"> · </span>
                  bestellt {fmtOrderDate(row.created_at)}
                </p>
              </div>
              <div className="shrink-0 text-right">
                <div
                  className={clsx(
                    "text-sm font-semibold tabular-nums",
                    row.days_open >= 60 ? "text-status-red" : "text-status-orange",
                  )}
                >
                  {age}
                </div>
                <div className="text-[11px] text-label-tertiary">offen</div>
              </div>
            </Link>
          </li>
        );
      })}
    </ul>
  );
}

export function HomeBestellwesenTile({ tile }: { tile: BestellwesenTileKey }) {
  const { user } = useCurrentUser();
  const { overview, loading, error } = useContext(HomeBestellwesenContext);
  const mine =
    userIsBuero(user) || userHasGroup(user, "App-Admins") || userHasGroup(user, "App-Tester");
  const staleDays = overview?.stale_order_days ?? 14;

  const section =
    tile === "bw-material-open"
      ? overview?.offene_materialanforderungen
      : tile === "bw-material-work"
        ? overview?.materialanforderungen_in_bearbeitung
        : tile === "bw-inquiries"
          ? overview?.offene_anfragen
          : overview?.ueberfaellige_bestellungen;

  const title =
    tile === "bw-material-open"
      ? "Offene Materialanforderungen"
      : tile === "bw-material-work"
        ? mine
          ? "Meine Materialanforderungen in Bearbeitung"
          : "Materialanforderungen in Bearbeitung"
        : tile === "bw-inquiries"
          ? "Offene Anfragen"
          : "Überfällige Bestellungen";

  const href =
    tile === "bw-inquiries"
      ? "/bestellwesen/anfragen"
      : tile === "bw-orders-late"
        ? "/bestellwesen/bestellungen"
        : "/bestellwesen/materialanforderungen";

  const linkLabel = tile === "bw-orders-late" ? "Zu Bestellungen" : "Alle anzeigen";
  const description =
    tile === "bw-inquiries"
      ? "Gesendet oder bereit, noch ohne Bestellung."
      : tile === "bw-orders-late"
        ? `Nicht als geliefert markiert und älter als ${staleDays} Tage.`
        : undefined;

  return (
    <Card
      className={tile === "bw-orders-late" ? "eh-bw-section--warn" : undefined}
      title={<CountTitle title={title} total={section?.total ?? 0} />}
      description={description}
      actions={<SectionLink href={href} label={linkLabel} />}
      bodyClassName="!p-0"
    >
      {loading && !overview ? (
        <div className="flex justify-center py-8">
          <Spinner />
        </div>
      ) : error && !section ? (
        <div className="p-4">
          <Alert variant="error">{error}</Alert>
        </div>
      ) : tile === "bw-inquiries" ? (
        <InquiryRows items={(section?.items as BestellwesenAnfrageOverviewItem[] | undefined) ?? []} />
      ) : tile === "bw-orders-late" ? (
        <OrderRows items={(section?.items as BestellwesenOrderOverviewItem[] | undefined) ?? []} />
      ) : (
        <MaRows items={(section?.items as MaListItem[] | undefined) ?? []} />
      )}
      {section ? <TruncatedHint section={section} /> : null}
    </Card>
  );
}
